<script lang="ts" setup>
import {
  computed,
  ref,
  PropType,
  watch,
  watchEffect,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import {
  ComputeTaskUtils,
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import {
  ComputeTaskCanonicalArtifactUrlDocument,
  ComputeTaskType,
} from '@/gql/graphql';
import apolloClient from '@/apollo/client';
import { ApolloError } from '@apollo/client/core';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCollapse,
  BFormCheckbox,
  BFormGroup,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import { IS_DEBUG_MODE, COMPUTE_TASK_STATUS } from '@/constants';
import type { TaskFileType } from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskFilesList from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskStatusBadge from '@/components/compute/ComputeTaskStatusBadge.vue';
import MermaidDiagram from '@/components/MermaidDiagram.vue';
import { buildProgressData } from '@/utils/nextflow-progress-data';
import {
  needsCanonicalProgress,
  shouldLoadCanonicalProgress,
  decodeCanonicalProgress,
} from '@/utils/nextflow-canonical-progress';

const userStore = useUserStore();

// --- Component Properties ---
const props = defineProps({
  task: { type: Object as PropType<ComputeTaskType> },
  expanded: { type: Boolean, default: false },
  selectedTab: {
    type: String as PropType<DataPipelineTaskTabOptionType>,
    default: DATA_PIPELINE_TASK_TAB_OPTIONS.FILES,
  },
});

// --- Emits ---
const emits = defineEmits<{
  (e: 'update:expanded', val: boolean): void;
  (e: 'update:selected-tab', val: DataPipelineTaskTabOptionType): void;
  (e: 'bucket-requested'): void;
  (e: 'exec-details-requested'): void;
  (e: 'debug-details-requested'): void;
  (e: 'abort-requested'): void;
  (e: 'directory-info-requested', file: TaskFileType): void;
  (e: 'directory-view-requested', file: TaskFileType): void;
  (e: 'file-preview-requested', file: TaskFileType): void;
  (e: 'file-download-requested', file: TaskFileType): void;
  (
    e: 'layout-needed',
    phase: 'start' | 'end' | 'resize',
    id?: number | null
  ): void;
}>();

// helper to emit layout events with the task id
function emitLayout(phase: 'start' | 'end' | 'resize') {
  emits('layout-needed', phase, props.task?.id ?? null);
}

// Observe size changes (images/tables loading) and notify parent
const cardRef = ref<InstanceType<typeof BCard> | null>(null);
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  const el = cardRef.value?.$el as HTMLElement | undefined;
  if (!el || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => emitLayout('resize'));
  resizeObserver.observe(el);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  requestVersion++;
});

// local reactive state ― decoupled from prop so the card can toggle itself
const selectedTab = ref(props.selectedTab);
const isExpanded = ref(props.expanded);
const detailedView = ref(true);
const exposeHiddenResults = ref(false);
const progress = computed(() => props.task?.results?.progressResults);
const archived = computed(() => needsCanonicalProgress(progress.value));
const archiveReady = computed(() =>
  shouldLoadCanonicalProgress(progress.value)
);
const archiveKey = computed(() =>
  props.task?.id
    ? `${props.task.id}:${
        progress.value?.canonicalPayloadArtifact?.sha256 || 'missing'
      }`
    : null
);
const archiveProcesses = ref<unknown[] | null>(null);
const loadedArchiveKey = ref<string | null>(null);
const loadingArchive = ref(false);
const archiveError = ref('');
const displayProcesses = computed(() =>
  archiveReady.value && archiveProcesses.value
    ? archiveProcesses.value
    : progress.value?.processes || []
);
const progressMappingOptions = computed(() => ({
  exposeHiddenResults: exposeHiddenResults.value,
  artifactLocation: {
    outputDir: props.task?.attributes?.stageOutput?.outputDir,
    bucketName: props.task?.bucket?.name,
  },
}));
const mappedArchiveProgress = computed(() =>
  buildProgressData(archiveProcesses.value || [], progressMappingOptions.value)
);
const mappedProgress = computed(() =>
  archiveReady.value && archiveProcesses.value
    ? mappedArchiveProgress.value
    : buildProgressData(displayProcesses.value, progressMappingOptions.value)
);
const mmdDiagram = computed(() => mappedProgress.value.mermaidDiagram ?? null);
let requestVersion = 0;
let pendingArchiveKey: string | null = null;

function archiveFailureText(error: unknown, taskId: number): string {
  const code =
    error instanceof ApolloError
      ? error.graphQLErrors.find((item) => item.extensions?.code)?.extensions
          ?.code
      : null;
  if (code === 'FORBIDDEN') {
    return 'You do not have access to these results. Ask a project owner for task access, then retry.';
  }
  if (code === 'NOT_FOUND')
    return 'This task was not found. Refresh the project page.';

  // Never render arbitrary exception text: a storage failure can contain a
  // signed URL. These are fixed messages created only by this component.
  const message = error instanceof Error ? error.message : '';
  if (message === 'archive-link-denied') {
    return 'The archive link expired or was denied. Retry to request a fresh link.';
  }
  if (message === 'archive-object-missing') {
    return `The archive file is missing. Retry, then contact support with task ${taskId} if it persists.`;
  }
  if (
    message === 'Archived results do not match this task.' ||
    message === 'Archived results are incomplete.' ||
    message === 'Archived result counts do not match this task.'
  ) {
    return `The archive failed verification. Retry, then contact support with task ${taskId} if it persists.`;
  }
  return `Archived results could not be loaded. Retry, then contact support with task ${taskId} if it persists.`;
}

async function loadArchive(force = false) {
  const id = props.task?.id;
  const key = archiveKey.value;
  const snapshot = progress.value;
  if (!isExpanded.value || !archiveReady.value) return;
  if (!id || !key || key.endsWith(':missing')) {
    archiveError.value =
      'The archive reference is missing. Refresh the project page, then contact support if it persists.';
    return;
  }
  if (!force && (loadedArchiveKey.value === key || pendingArchiveKey === key)) {
    return;
  }

  const version = ++requestVersion;
  pendingArchiveKey = key;
  loadingArchive.value = true;
  archiveError.value = '';
  try {
    const { data } = await apolloClient.query({
      query: ComputeTaskCanonicalArtifactUrlDocument,
      variables: { id },
      fetchPolicy: 'no-cache',
      context: { fetchOptions: { cache: 'no-store' } },
    });
    const url = data.computeTaskCanonicalArtifactUrl;
    if (!url) throw new Error('Archived results are unavailable.');
    const response = await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    });
    if (!response.ok) {
      throw new Error(
        response.status === 401 || response.status === 403
          ? 'archive-link-denied'
          : response.status === 404
          ? 'archive-object-missing'
          : 'archive-fetch-failed'
      );
    }
    const processes = await decodeCanonicalProgress(
      snapshot,
      await response.arrayBuffer()
    );
    if (version !== requestVersion || archiveKey.value !== key) return;
    archiveProcesses.value = processes;
    loadedArchiveKey.value = key;
  } catch (error) {
    if (version !== requestVersion) return;
    archiveError.value = archiveFailureText(error, id);
  } finally {
    if (version === requestVersion) {
      pendingArchiveKey = null;
      loadingArchive.value = false;
    }
  }
}

watch(
  [isExpanded, archiveReady, archiveKey],
  ([expanded, needed, key], previous) => {
    if (key !== previous?.[2]) {
      requestVersion++;
      pendingArchiveKey = null;
      if (key?.split(':')[0] !== previous?.[2]?.split(':')[0]) {
        archiveProcesses.value = null;
        loadedArchiveKey.value = null;
      }
      loadingArchive.value = false;
      archiveError.value = '';
    }
    if (expanded && needed) void loadArchive();
  },
  { immediate: true }
);

watchEffect(
  () => (
    (isExpanded.value = props.expanded),
    console.debug('isExpanded set to', isExpanded.value, 'from prop')
  )
);
watchEffect(
  () => (
    (selectedTab.value = props.selectedTab),
    console.debug('selectedTab set to', selectedTab.value, 'from prop')
  )
);

function selectTab(tab: DataPipelineTaskTabOptionType) {
  selectedTab.value = tab;
  emits('update:selected-tab', selectedTab.value);
}

function toggleCard() {
  isExpanded.value = !isExpanded.value;
  emits('update:expanded', isExpanded.value);
}

function expandCard() {
  if (!isExpanded.value) {
    toggleCard();
  }
}

const progressFields = [
  { key: 'label', label: 'Item' },
  { key: 'value', label: 'Value', tdClass: 'text-end font-monospace' },
];
</script>
<template>
  <div class="col" v-if="props.task?.id">
    <BCard
      ref="cardRef"
      :class="[
        'd-flex flex-column p-0 card-sample',
        isExpanded ? 'expanded' : 'collapsed h-100',
      ]"
    >
      <slot>
        <!-- Task Header: Tile+Subtitle | ActionButtons -->
        <div
          class="d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2"
        >
          <!-- Task Header Left: Title+Subtitle -->
          <div class="me-sm-3 flex-grow-1">
            <!-- Task Name -->
            <h2
              @click="toggleCard"
              :id="`task-title-${props.task.id}`"
              class="mb-0 task-title"
              v-b-tooltip.hover
              :title="isExpanded ? 'Click to collapse' : 'Click to expand'"
            >
              <ComputeTaskStatusBadge
                v-if="!isExpanded"
                :task="props.task"
                icon-only
              />
              <!-- Task Name-->
              {{ props.task.name }}
              <BBadge variant="warning" class="ms-2">
                <small>
                  <i class="fab fa-neos"></i>
                  Nextflow
                </small>
              </BBadge>
              <!-- Collapse icon -->
              <font-awesome-icon
                v-if="isExpanded"
                icon="chevron-up"
                class="ms-2"
              />
            </h2>
            <!-- Progress Text Description / Error Message -->
            <div class="my-2">
              <ComputeTaskStatusBadge :task="props.task" />
            </div>
          </div>
          <!-- Task Header Right: Show More / Action Buttons -->
          <div class="mb-2 ms-sm-auto">
            <b-button
              @click="expandCard"
              v-if="!isExpanded"
              variant="link"
              size="sm"
              class="p-0 m-0 link-secondary text-decoration-none"
            >
              Show More
              <font-awesome-icon icon="chevron-down" class="ms-1" />
            </b-button>
            <!-- Action buttons -->
            <b-button-group v-if="isExpanded">
              <!-- Diagram -->
              <b-button
                :hidden="!mmdDiagram"
                v-if="props.task.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  expandCard();
                  selectTab(DATA_PIPELINE_TASK_TAB_OPTIONS.DIAGRAM);
                "
                :id="`btn-diagram-${props.task.id}`"
                v-b-tooltip.hover
                title="View diagram"
                :active="
                  isExpanded &&
                  selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.DIAGRAM
                "
              >
                <font-awesome-icon icon="diagram-project" />
              </b-button>
              <!-- Progress Timeline Link -->
              <b-button
                v-if="props.task.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  expandCard();
                  selectTab(DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS);
                "
                :id="`btn-progress-${props.task.id}`"
                v-b-tooltip.hover
                title="View progress timeline with stats"
                :active="
                  isExpanded &&
                  selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS
                "
              >
                <font-awesome-icon icon="list-check" />
              </b-button>
              <!-- Figures -->
              <b-button
                hidden
                v-if="props.task.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  expandCard();
                  selectTab(DATA_PIPELINE_TASK_TAB_OPTIONS.FIGURES);
                "
                :id="`btn-figures-${props.task.id}`"
                v-b-tooltip.hover
                title="View figures"
                :active="
                  isExpanded &&
                  selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FIGURES
                "
              >
                <font-awesome-icon icon="chart-line" />
              </b-button>
              <!-- Files List -->
              <b-button
                v-if="props.task.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  expandCard();
                  selectTab(DATA_PIPELINE_TASK_TAB_OPTIONS.FILES);
                "
                :id="`btn-files-${props.task.id}`"
                v-b-tooltip.hover
                title="View files"
                :active="
                  isExpanded &&
                  selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
                "
              >
                <font-awesome-icon icon="file" />
              </b-button>
              <!-- Exec Configuration -->
              <BButton
                variant="outline-dark"
                size="sm"
                @click="emits('exec-details-requested')"
                :id="`btn-exec-${props.task.id}`"
                v-b-tooltip.hover
                title="View exec configuration"
              >
                <font-awesome-icon :icon="['fas', 'sliders']" />
              </BButton>
              <!-- Bucket Link for Developers -->
              <BButton
                v-if="IS_DEBUG_MODE || userStore.developer"
                variant="outline-dark"
                size="sm"
                @click="emits('bucket-requested')"
                :id="`btn-bucket-${props.task.id}`"
                :disabled="!props.task.bucket"
                v-b-tooltip.hover
                title="Go to data"
              >
                <font-awesome-icon :icon="['fas', 'database']" />
              </BButton>
              <!-- Debug Details -->
              <BButton
                v-if="IS_DEBUG_MODE || userStore.developer"
                variant="outline-dark"
                size="sm"
                @click="emits('debug-details-requested')"
                :id="`btn-debug-${props.task.id}`"
                v-b-tooltip.hover
                title="View debug details"
              >
                <font-awesome-icon :icon="['fas', 'code']" />
              </BButton>
              <!-- Abort Compute Task -->
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="!ComputeTaskUtils.canBeAborted(props.task.status)"
                @click="emits('abort-requested')"
                :id="`btn-abort-${props.task.id}`"
                v-b-tooltip.hover
                title="Abort task"
                :active="props.task.status === COMPUTE_TASK_STATUS.ABORTING"
              >
                <font-awesome-icon icon="times" />
              </b-button>
            </b-button-group>
          </div>
          <!-- end of action buttons group div -->
        </div>
        <!-- end of task header row -->

        <!-- Collapsible Task Content -->
        <BCollapse
          v-model="isExpanded"
          class="mt-2 flex-grow-1"
          @show="emitLayout('start')"
          @hide="emitLayout('start')"
          @shown="emitLayout('end')"
          @hidden="emitLayout('end')"
        >
          <BAlert
            v-if="mappedProgress.artifactPathError"
            :model-value="true"
            variant="warning"
          >
            Some result files have an invalid storage location. Refresh the
            project page, then contact support if it persists.
          </BAlert>
          <BAlert
            v-if="
              archiveError &&
              archiveProcesses &&
              loadedArchiveKey !== archiveKey
            "
            :model-value="true"
            variant="info"
            >Showing the previously verified results while the latest archive is
            unavailable.</BAlert
          >
          <BAlert
            v-if="archived"
            :model-value="true"
            :variant="archiveError ? 'danger' : 'info'"
          >
            <span v-if="loadingArchive">Loading archived results...</span>
            <span v-else-if="archiveError">{{ archiveError }}</span>
            <span v-else-if="loadedArchiveKey === archiveKey"
              >Archived results loaded.</span
            >
            <span v-else-if="!archiveReady"
              >Inline results will keep updating; archived results load when
              collection finishes.</span
            >
            <span v-else>Results are stored in an archive.</span>
            <BButton
              v-if="archiveError"
              size="sm"
              variant="outline-dark"
              class="ms-2"
              @click="loadArchive(true)"
              >Retry</BButton
            >
          </BAlert>
          <!-- MMD DIAGROM -->
          <template
            v-if="selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.DIAGRAM"
          >
            <div v-if="!mmdDiagram" class="text-muted table-border mb-1">
              No diagram available for this task.
            </div>
            <MermaidDiagram
              v-else
              :source="mmdDiagram"
              :diagram-id="props.task?.id"
              @rendered="emitLayout('end')"
            />
          </template>
          <!-- END OF MMD DIAGRAM -->
          <!-- Processes - Results / Files - Timeline -->
          <template v-else-if="displayProcesses.length || archived">
            <div
              class="d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2"
            >
              <!-- Left: Heading Progress / Files with Expand/Collapse feature -->
              <div class="me-sm-3 flex-grow-1">
                <h3
                  @click="detailedView = !detailedView"
                  style="cursor: pointer"
                  :id="`progress-title-${props.task.id}`"
                >
                  <template
                    v-if="
                      selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS
                    "
                  >
                    Progress
                  </template>
                  <template
                    v-else-if="
                      selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
                    "
                  >
                    Files
                  </template>
                  <i
                    :class="
                      detailedView
                        ? ['ms-1', 'bi', 'bi-arrows-collapse']
                        : ['ms-1', 'bi', 'bi-arrows-expand']
                    "
                  />
                </h3>
                <BTooltip
                  :target="`progress-title-${props.task.id}`"
                  placement="top-start"
                  triggers="hover"
                >
                  <template #default>
                    {{ detailedView ? 'Click to collapse' : 'Click to expand' }}
                  </template>
                </BTooltip>
              </div>
              <!-- Right: Toggle Expose Hidden Results -->
              <div
                class="ms-sm-auto"
                :id="`form-group-toggle-hidden-results-${props.task.id}`"
              >
                <BFormGroup class="d-flex flex-nowrap align-items-center">
                  <label
                    :for="`toggle-hidden-results-${props.task.id}`"
                    class="small"
                    >Show more</label
                  >
                  <BFormCheckbox
                    v-model="exposeHiddenResults"
                    size="sm"
                    class="ms-1"
                    :id="`toggle-hidden-results-${props.task.id}`"
                    switch
                  />
                </BFormGroup>
              </div>
              <BTooltip
                :target="`form-group-toggle-hidden-results-${props.task.id}`"
                triggers="hover"
                placement="left"
              >
                <template #default> Expose hidden results </template>
              </BTooltip>
            </div>
            <!-- ROUTE / TIMELINE / PROGRESS / RESULTS -->
            <div class="route">
              <div
                v-for="(proc, i) in mappedProgress.processes"
                :key="i"
                class="route__section"
              >
                <!-- circular point -->
                <div class="route__marker" :class="proc.state">
                  <span class="visually-hidden"
                    >ProgressSection {{ i + 1 }}</span
                  >
                </div>

                <!-- right side body -->
                <div class="route__body">
                  <h5 class="route__title mb-1">
                    <span v-if="proc.sampleId"> {{ proc.sampleId }}: </span>
                    {{ proc.name }}
                  </h5>

                  <b-collapse
                    v-model="detailedView"
                    @show="emitLayout('start')"
                    @hide="emitLayout('start')"
                    @shown="emitLayout('end')"
                    @hidden="emitLayout('end')"
                  >
                    <!-- PROGRESS RESULTS TABLE -->
                    <div
                      v-if="
                        selectedTab ===
                          DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS &&
                        proc.results?.length
                      "
                      class="table-border mb-1"
                    >
                      <BTable
                        :items="proc.results"
                        :fields="progressFields"
                        small
                        striped
                        hover
                        responsive="sm"
                        stacked="sm"
                        class="browser-table route__table mb-0"
                        thead-class="visually-hidden"
                      >
                        <!-- show the raw label -->
                        <template #cell(label)="{ item }">
                          {{ item.label }}
                        </template>

                        <!-- TODO: format numeric values per your locale/row rules -->
                        <template #cell(value)="{ item }">
                          {{ item.value }}
                        </template>
                      </BTable>
                    </div>
                    <!-- END OF PROGRESS RESULTS TABLE -->
                    <!-- FILES -->
                    <div
                      v-if="
                        selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FILES &&
                        proc.files?.length
                      "
                      class="table-border mb-1"
                    >
                      <ComputeTaskFilesList
                        :files="proc.files"
                        @directory-info-requested="
                          emits('directory-info-requested', $event)
                        "
                        @directory-view-requested="
                          emits('directory-view-requested', $event)
                        "
                        @file-preview-requested="
                          emits('file-preview-requested', $event)
                        "
                        @file-download-requested="
                          emits('file-download-requested', $event)
                        "
                      />
                    </div>
                    <!-- END OF FILES -->
                  </b-collapse>
                </div>
              </div>
            </div>
            <!-- End of Timeline Div -->
          </template>
          <!-- End of Progress Timeline Template -->

          <!-- End of Files Div -->
        </BCollapse>
        <!-- End of Collapsible Task Content -->
      </slot>
    </BCard>
    <!-- End of Task Card-->
  </div>
</template>
<style scoped>
div.table-border {
  padding: 5px;
  border-radius: 5px;
  background-color: #fff;
  border: 1px solid #dee2e6;
}
.card-sample {
  display: flex;
  background-color: #f5f5f5;
  overflow: visible;

  h2 {
    font-size: 1rem;
    font-weight: bold;
  }

  h2.task-title {
    cursor: pointer;
  }

  h3 {
    font-size: 0.8rem;
    font-weight: bold;
  }

  h5 {
    font-size: 0.8rem;
    font-weight: bold;
  }
}

/* --- Route Timeline --- */
.route {
  --route-color-active: var(--bs-primary);
  --route-color-pending: var(--bs-secondary);
  --route-color-error: var(--bs-danger);
  --route-color-done: var(--bs-success);

  --route-color-line: var(--bs-border-color, #d0d7de);
  --route-color-dot: #fff;

  --route-gap: 0.25rem;
  --route-dot-size: 14px;
  --route-line-width: 2px;
  --route-marker-nudge: 1px; /* + moves dot down, - moves dot up */
}

/* Contain the vertical guide line and any absolutely-positioned dots */
.route {
  position: relative;
}

/* If you draw the vertical line with ::before, anchor it to the card, not a fragment */
.route::before {
  top: 0;
  bottom: 0;
}

/* one section row: two columns -> [marker] [content] */
.route__section {
  position: relative;
  display: grid;
  grid-template-columns: calc(var(--route-dot-size) + 6px) 1fr;
  align-items: start;
  column-gap: var(--route-gap);
}

/* vertical dashed connector (segment below the current dot) */
.route__section::after {
  content: '';
  position: absolute;
  left: calc((var(--route-dot-size) + 6px) / 2 - var(--route-line-width) / 2);
  top: calc(var(--route-dot-size) + 4px + var(--route-marker-nudge));
  bottom: 0;
  border-left: var(--route-line-width) dashed var(--route-color-line);
}
.route__section:last-child::after {
  display: none;
} /* no line after last dot */

/* the circular dot */
.route__marker {
  position: relative;
  width: calc(var(--route-dot-size) + 6px);
  height: var(--route-dot-size);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: var(--route-marker-nudge);
}
.route__marker::before {
  content: '';
  width: var(--route-dot-size);
  height: var(--route-dot-size);
  border-radius: 50%;
  background: var(--route-color-dot);
  box-shadow: 0 0 0 3px var(--route-color-line) inset;
}

/* Heartbeat + ripple when in progress */
.route__marker.active {
  position: relative;
  overflow: visible; /* let ripple grow outside the cell */
  will-change: transform;
}

/* optional states (colors of the ring) */
.route__marker.pending::before {
  box-shadow: 0 0 0 3px var(--route-color-line) inset;
}
.route__marker.active::before {
  box-shadow: 0 0 0 3px var(--route-color-active) inset;
  /* background: var(--route-color-active); */
  /* the dot: grow/shrink */
  animation: route-beat 1.1s ease-in-out infinite;
  transform-origin: center;
}
.route__marker.done::before {
  box-shadow: 0 0 0 3px var(--route-color-done) inset;
  /* background: var(--route-color-done); */
}
.route__marker.error::before {
  box-shadow: 0 0 0 3px var(--route-color-error) inset;
}
/* Skipped is terminal, not waiting: same ring as pending but faded, so it
   reads as "never ran" rather than "still queued". */
.route__marker.skipped::before {
  box-shadow: 0 0 0 3px var(--route-color-line) inset;
  opacity: 0.45;
}

/* the ripple ring */
.route__marker.active::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: var(--route-dot-size);
  height: var(--route-dot-size);
  transform-origin: center;
  border-radius: 50%;
  border: 1px solid var(--route-color-active); /* animated ring color */
  opacity: 0.35;
  transform: scale(1);
  animation: route-ripple 1.1s ease-out infinite;
  pointer-events: none;
}

/* route title */
.route__title {
  line-height: 1.3;
}

/* definition list rows */
.route__stats {
  margin: 0;
}
.route__row {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}
.route__row dt {
  font-weight: 600;
}
.route__row dd {
  margin: 0;
}

/* keyframes */
@keyframes route-beat {
  0%,
  100% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.15);
  }
  60% {
    transform: scale(1.05);
  }
}

@keyframes route-ripple {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.35;
  }
  70% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
  }
}

/* Respect reduced-motion user preference */
@media (prefers-reduced-motion: reduce) {
  .route__marker.inprogress::before,
  .route__marker.inprogress::after {
    animation: none !important;
  }
}
</style>
